import { api } from './client'

/**
 * F-073 (TASK-0327): client della salute della piattaforma (solo Admin). Le chiamate a jobs e outbox health sono
 * dichiarate qui apposta, senza toccare `jobs.ts` e `outbox.ts`.
 */
export type MetricsWindow = '15m' | '1h' | '24h'

export interface CategoryMetrics {
  category: 'Navigazione' | 'Ricerca' | 'Dashboard' | 'ApiInterne' | 'ApiEsterne'
  label: string
  requests: number
  p50Ms: number | null
  p95Ms: number | null
  p99Ms: number | null
  targetMs: number
  /** null senza richieste. */
  withinTarget: boolean | null
  /** Frazioni 0..1; null senza richieste. */
  errorRate: number | null
  clientErrorRate: number | null
  successRate: number | null
}

export interface SlowRoute {
  route: string
  category: string
  requests: number
  p95Ms: number | null
  p99Ms: number | null
  errorRate: number | null
}

export interface AvailabilityMetrics {
  since: string
  sampledMinutes: number
  upMinutes: number
  percent: number | null
  lastDown: string | null
}

/** F-115 (REQ-302-001): storico su Redis; il minuto senza campione conta come fermo. */
export interface AvailabilityHistoryWindow {
  label: string
  windowMinutes: number
  since: string | null
  upMinutes: number
  downMinutes: number
  noSampleMinutes: number
  percent: number | null
}

export type AvailabilityHistory =
  | { enabled: false }
  | { enabled: true; available: false; error: string }
  | {
      enabled: true
      available: true
      last24Hours: AvailabilityHistoryWindow
      last7Days: AvailabilityHistoryWindow
      last30Days: AvailabilityHistoryWindow
    }

export interface PlatformMetrics {
  window: MetricsWindow
  windowMinutes: number
  generatedAt: string
  processStartedAt: string
  categories: CategoryMetrics[]
  slowRoutes: SlowRoute[]
  availability: AvailabilityMetrics
  availabilityHistory?: AvailabilityHistory
  notes: string[]
}

export interface PlatformHealthReport {
  databaseLoad: {
    available: boolean
    connections: {
      active: number
      idleInTransaction: number
      idle: number
      total: number
      max: number
      utilizationPercent: number | null
    } | null
    waitingLocks: number | null
    databaseSizeBytes: number | null
    averageQueryTimeMs: number | null
    reason: string | null
  }
  storage: {
    disk: { available: boolean; totalBytes: number | null; freeBytes: number | null; usedPercent: number | null; reason: string | null }
    backupDirectory: { available: boolean; sizeBytes: number | null; fileCount: number | null; reason: string | null }
    logDirectory: { available: boolean; sizeBytes: number | null; fileCount: number | null; reason: string | null }
    documentStorage: { available: boolean; reason: string | null }
  }
  backupStatus: {
    state: string
    lastSuccessAt: string | null
    ageHours: number | null
    isLate: boolean | null
    reason: string | null
  }
}

export interface JobsHealthSummary {
  pending: number
  deadLetterCount: number
  oldestPendingAgeSeconds: number | null
  processedLast24h: number
  failedLast24h: number
  jobFailureRatePercent: number | null
  outboxDeadLetterCount: number
  combinedDeadLetterCount: number
}

export interface OutboxHealthSummary {
  pending: number
  failed: number
  oldestPendingAgeSeconds: number | null
  processedLast24h: number
  permanentlyFailedLast24h: number
}

/** F-107 (TASK-0361): stato della cache in memoria (solo contatori, nessun valore). */
export interface CacheRegionStatus {
  region: string
  hits: number
  misses: number
  entries: number
  /** Frazione 0..1; null senza richieste. */
  hitRate: number | null
  lastInvalidatedAt: string | null
}

export interface CacheStatus {
  referenceDataEnabled: boolean
  dashboardsEnabled: boolean
  referenceDataTtlSeconds: number
  dashboardsTtlSeconds: number
  regions: CacheRegionStatus[]
}

export const platformHealthApi = {
  metrics: (window: MetricsWindow) => api<PlatformMetrics>(`/api/platform-health/metrics?window=${window}`),
  report: () => api<PlatformHealthReport>('/api/platform-health'),
  jobs: () => api<JobsHealthSummary>('/api/jobs/health'),
  outbox: () => api<OutboxHealthSummary>('/api/outbox/health'),
  cache: () => api<CacheStatus>('/api/platform/cache'),
  clearCache: (region?: string) =>
    api<{ cleared: string }>(`/api/platform/cache/clear${region ? `?region=${encodeURIComponent(region)}` : ''}`, { method: 'POST' }),
}
